import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Centralized API client.
 *
 * Error shape (consumers can rely on err.status + err.code):
 *   - 401  → code="unauthorized" (redirected to /login except on auth pages)
 *   - 403  → code="forbidden"    (RouteErrorBoundary shows "No access" UI)
 *   - 404  → code="not_found"
 *   - 422  → code="validation"
 *   - 5xx  → code="server_error"
 *   - network/offline → code="network"
 */
export async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (e) {
    // TypeError: Failed to fetch — offline, DNS fail, server down, CORS preflight fail
    const err = new Error('Unable to reach server. Check your connection.');
    err.code = 'network';
    err.status = 0;
    err.cause = e;
    throw err;
  }

  if (response.status === 401) {
    // If the caller is the login/forgot-password page itself, surface the
    // backend's proper error message ("Wrong username or password") to the
    // UI instead of forcibly logging out and reloading — otherwise the
    // toast never gets a chance to render.
    const onAuthPage =
      typeof window !== 'undefined' &&
      /\/(login|forgot-password|reset-password)/.test(window.location.pathname);

    if (!onAuthPage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      const err = new Error('Unauthorized');
      err.status = 401;
      err.code = 'unauthorized';
      throw err;
    }

    // Auth page → surface backend detail so the user sees exactly what's wrong.
    const body = await response.json().catch(() => ({ detail: 'Wrong username or password' }));
    const err = new Error(body.detail || 'Wrong username or password');
    err.status = 401;
    err.code = 'unauthorized';
    throw err;
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: 'An error occurred' }));
    const err = new Error(body.detail || `Request failed (${response.status})`);
    err.status = response.status;
    if (response.status === 403) err.code = 'forbidden';
    else if (response.status === 404) err.code = 'not_found';
    else if (response.status === 422) err.code = 'validation';
    else if (response.status >= 500) err.code = 'server_error';
    else err.code = 'http_error';
    throw err;
  }

  if (response.status === 204) {
    return null;
  }

  // Side-effect: any successful mutation that affects unread state should
  // refresh the OS app icon badge. Fire a synthetic event the badge hook
  // listens for. Keeping this in the API client (not at every call site)
  // means new mark-as-read code paths get badge sync for free.
  try {
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      const path = endpoint.split('?')[0];
      const triggersBadge =
        /\/notifications\/.+\/read$/.test(path) ||
        /\/notifications\/(mark-all-read|read-all)$/.test(path) ||
        /\/notifications\/clear-all$/.test(path) ||
        /\/chat\/conversations\/.+\/read$/.test(path) ||
        path === '/notifications';
      if (triggersBadge && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pie:badge-invalidate'));
      }
    }
  } catch (_) {
    /* ignore */
  }

  return response.json();
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getPriorityColor(priority) {
  const colors = {
    low: 'bg-slate-100 text-slate-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
  };
  return colors[priority] || colors.medium;
}

export function getStatusColor(status) {
  const colors = {
    not_started: 'bg-slate-100 text-slate-700',
    in_progress: 'bg-blue-100 text-blue-800',
    blocked: 'bg-orange-100 text-orange-800',
    done: 'bg-green-100 text-green-800',
    needs_review: 'bg-purple-100 text-purple-800',
  };
  return colors[status] || colors.not_started;
}

export function getStatusLabel(status) {
  const labels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    blocked: 'Blocked',
    done: 'Done',
    needs_review: 'Needs Review',
  };
  return labels[status] || status;
}

export function getPriorityLabel(priority) {
  const labels = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  };
  return labels[priority] || priority;
}

// Format date to YYYY-MM-DD using local timezone (not UTC)
export function formatDateLocal(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Get today's date in YYYY-MM-DD format (local timezone)
export function getTodayLocal() {
  return formatDateLocal(new Date());
}

export function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = getTodayLocal();
  return dueDate < today;
}

export function isDueToday(dueDate) {
  if (!dueDate) return false;
  const today = getTodayLocal();
  return dueDate === today;
}
